import {Observable, mergeMap} from 'rxjs';
import Keycloak, {KeycloakInitOptions} from 'keycloak-js';
import {EnvironmentProviders, inject, Injector, makeEnvironmentProviders, runInInjectionContext} from '@angular/core';
import {DOCUMENT} from "@angular/common";
import {createInterceptorCondition, INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, IncludeBearerTokenCondition} from "keycloak-angular";
import {ConfigService} from '@shared/services';
import {UserProfileService} from '@shared/services/api/user-profile.service';
import {SegmentService} from 'ngx-segment-analytics';
import {FrontendDomainConfig} from '@app/shared/services/api/dashboard-config.interface';
import {LOGIN_REDIRECT_PATH} from '@shared/services/routing/login-redirect.service';

declare global {
  interface Window {
    getKeycloakTokens: () => {token: string, refreshToken: string};
  }
}

enum KeycloakLoginType {
  LOGIN_REQUIRED = 'login-required',
  CHECK_SSO = 'check-sso'
}

const NON_FORCED_LOGIN_PATHS = ['public', LOGIN_REDIRECT_PATH];

export const customProvideKeycloak: EnvironmentProviders = makeEnvironmentProviders([
  {
    provide: Keycloak,
    useFactory: () => {
      const config = inject(ConfigService).getCachedConfig();
      const keycloakConfig = config.keycloak['js-adapter-config'];
      return new Keycloak({
        clientId: keycloakConfig.clientId,
        realm: keycloakConfig.realm,
        url: keycloakConfig.url
      });
    }
  },
  {
    provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
    useFactory: (): IncludeBearerTokenCondition[] => {
      const windowRef = inject(DOCUMENT).defaultView;
      if (windowRef) {
        const currentDomain = new URL(windowRef.location.href).host;
        const urlPatterns = [
          '((http(s?)://' + currentDomain + ')?/api/.*)',
          // uncomment line below to include bearer token in all localhost traffics when dev
          // '(http://localhost:\\d+/.*)'
        ];
        return [createInterceptorCondition<IncludeBearerTokenCondition>({urlPattern: new RegExp('^' + urlPatterns.join('|') + '$', 'i')})];
      } else {
        throw new Error('window could not be accessed');
      }
    }
  }
]);

function getKeycloakInitOptions(windowRef: Window, domainConfig: FrontendDomainConfig | null): KeycloakInitOptions {
  // In order to support showing pages to non-logged in users, need to initialise keycloak with 'check-sso' instead of 'login-required'.
  // This will redirect the browser to Keycloak server to check if the user is logged in (this check is done by the Keycloak server by
  // checking for a valid Keycloak SSO cookie). The problem is that pages that require a login, will the browser redirect back to
  // Keycloak server again.
  // In order to avoid double redirect to Keycloak server, all the pages that can be accessed by non-logged in users will be placed
  // under /public URL path. The /login-redirect path joins them because Keycloak returns non-logged-in users there too,
  // on their way to a page that never needed a login. The code below uses 'check-sso' for those paths, and
  // 'login-required' for all other URL paths.
  const baseUrlPath = new URL(windowRef.location.origin).pathname;
  const normalisedBasePath = baseUrlPath ? (baseUrlPath + (baseUrlPath.endsWith('/') ? '' : '/')) : '/';
  const path = windowRef.location.pathname;
  const ssoIdpHint = domainConfig?.sso?.keycloak?.idp_hint;
  const forceLogin = ssoIdpHint
    ? false
    : !NON_FORCED_LOGIN_PATHS.some(nonForcedLoginPath => {
        const fullPath = normalisedBasePath + nonForcedLoginPath;
        return path === fullPath || path.startsWith(fullPath + '/');
      });

  let tokens;
  // Authentication tokens can be passed in from the browser automation jobs, e.g. screenshot code or tests, which use browser
  // specific methods (e.g. Chrome DevTools) to inject the definition of the getKeycloakTokens() function, and making it return
  // valid authentication tokens.
  // So if the function is defined, invoke it, and pass its returned values to Keycloak
  if (typeof windowRef.getKeycloakTokens === 'function') {
    tokens = windowRef.getKeycloakTokens();
  }

  return {
    onLoad: forceLogin ? KeycloakLoginType.LOGIN_REQUIRED : KeycloakLoginType.CHECK_SSO,
    ...tokens,
    checkLoginIframe: false
  };
}

export function initializer(): Observable<any> {
  const injector = inject(Injector);
  const configService = inject(ConfigService);
  return configService.fetchAndCacheConfig().pipe(mergeMap((config) => runInInjectionContext(injector, () => {
    const windowRef = inject(DOCUMENT).defaultView;
    const hostname = windowRef.location.hostname;
    const domainConfig = config.domains?.find(({domain_name}) => domain_name.toLowerCase() === hostname.toLowerCase()) ?? null;
    const intercomEnabled = domainConfig?.segment?.features?.intercom !== false;
    inject(SegmentService).load(
      {writeKey: config.segment.ref, cdnURL: config.segment.host},
      {...!intercomEnabled && {integrations: { Intercom: false }}}
    );
    const keycloakInitOptions = getKeycloakInitOptions(windowRef, domainConfig);
    // UserProfileService is injected here and not sooner so that its dependency, Keycloak, will only be
    // initialised now, after the config has been fetched
    return inject(UserProfileService).init(keycloakInitOptions, domainConfig);
  })));
}
